import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { getToken } from '../../utils/auth';
import './Invoices.css';

const STATUS_BADGE = {
  true:  { label: 'Active',  cls: 'badge-active'  },
  false: { label: 'Expired', cls: 'badge-expired' },
};

const fmt = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtCurrency = (n) =>
  n != null ? `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—';

const Invoices = () => {
  const [invoices, setInvoices]     = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch]         = useState('');
  const [status, setStatus]         = useState('');
  const [loading, setLoading]       = useState(false);
  const [downloading, setDownloading] = useState(null); // subscriptionId being downloaded
  const [previewUrl, setPreviewUrl] = useState(null);

  const LIMIT = 20;
  const API_BASE = 'https://api.bandhanguru.com';

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      const res = await api.get(`/admin/invoices?${params}`);
      setInvoices(res.data.data || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [page, search, status]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchInvoices();
  };

  const handleDownload = async (inv) => {
    setDownloading(inv._id);
    try {
      const token = getToken();
      const res = await fetch(
        `${API_BASE}/api/admin/invoices/${inv._id}/download`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `invoice_${inv.invoiceNumber.replace(/\//g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download invoice');
    } finally {
      setDownloading(null);
    }
  };

  const handlePreview = (inv) => {
    const token = getToken();
    setPreviewUrl(`${API_BASE}/api/admin/invoices/${inv._id}/preview?token=${token}`);
  };

  return (
    <div className="inv-container">
      {/* ── Header ── */}
      <div className="inv-header">
        <div>
          <h1>Invoice Management</h1>
          <p>{total} invoice{total !== 1 ? 's' : ''} found</p>
        </div>
      </div>

      {/* ── Filters ── */}
      <form className="inv-filters" onSubmit={handleSearch}>
        <input
          className="inv-search"
          placeholder="Search by vendor, email, plan, payment ID…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="inv-select" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
        </select>
        <button type="submit" className="inv-btn-search">Search</button>
      </form>

      {/* ── Table ── */}
      <div className="inv-table-wrap">
        {loading ? (
          <div className="inv-loading">Loading invoices…</div>
        ) : invoices.length === 0 ? (
          <div className="inv-empty">
            <div style={{ fontSize: 48 }}>🧾</div>
            <h3>No invoices found</h3>
            <p>Invoices are generated automatically when vendors subscribe to a plan.</p>
          </div>
        ) : (
          <table className="inv-table">
            <thead>
              <tr>
                <th>Invoice No.</th>
                <th>Vendor</th>
                <th>Plan</th>
                <th>Amount</th>
                <th>Payment ID</th>
                <th>Date</th>
                <th>Valid Until</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => {
                const badge = STATUS_BADGE[String(inv.isActive)] || STATUS_BADGE.false;
                return (
                  <tr key={inv._id}>
                    <td className="inv-number">{inv.invoiceNumber}</td>
                    <td>
                      <div className="inv-vendor-name">{inv.vendorName || '—'}</div>
                      <div className="inv-vendor-email">{inv.vendorEmail || ''}</div>
                    </td>
                    <td>{inv.planName || '—'}</td>
                    <td className="inv-amount">{fmtCurrency(inv.amountPaid)}</td>
                    <td className="inv-txn">{inv.paymentId || '—'}</td>
                    <td>{fmt(inv.createdAt)}</td>
                    <td>{fmt(inv.endDate)}</td>
                    <td><span className={`inv-badge ${badge.cls}`}>{badge.label}</span></td>
                    <td>
                      <div className="inv-actions">
                        <button
                          className="inv-btn-preview"
                          onClick={() => handlePreview(inv)}
                          title="Preview Invoice"
                        >👁 Preview</button>
                        <button
                          className="inv-btn-download"
                          onClick={() => handleDownload(inv)}
                          disabled={downloading === inv._id}
                          title="Download PDF"
                        >
                          {downloading === inv._id ? '⏳' : '⬇ PDF'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="inv-pagination">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span>Page {page} of {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}

      {/* ── Preview Modal ── */}
      {previewUrl && (
        <div className="inv-modal-overlay" onClick={() => setPreviewUrl(null)}>
          <div className="inv-modal" onClick={e => e.stopPropagation()}>
            <div className="inv-modal-header">
              <h2>Invoice Preview</h2>
              <div className="inv-modal-actions">
                <button className="inv-btn-download" onClick={() => window.open(previewUrl, '_blank')}>
                  Open in New Tab
                </button>
                <button className="inv-btn-close" onClick={() => setPreviewUrl(null)}>✕ Close</button>
              </div>
            </div>
            <iframe
              src={previewUrl}
              title="Invoice Preview"
              className="inv-iframe"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
